import type { UFOInteractionContextType } from '@atlaskit/react-ufo/interaction-context';

import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { getLogger } from '@confluence/logger';
import { fg } from '@confluence/feature-gating';

import { type Match } from './RouteManager';

type TransitionCallbackArgs = { match: Match };
type TransitionCallback = (args: TransitionCallbackArgs) => Promise<void>;
type OnPerformanceMetricStartCallback = (args: { match: Match }) => void;
type CallbackConfig = {
	callback: TransitionCallback;
	onlyCalledIfFunc?: OnlyCalledIfFunc;
	onRouteContinueCallback?: OnRouteContinueCallback;
	onPerformanceMetricStart?: OnPerformanceMetricStartCallback;
};
type OnRouteContinueCallback = (args: { match: Match }) => Promise<void>;
type OnlyCalledIfFuncArgs = { match: Match };
type OnlyCalledIfFunc = (args: OnlyCalledIfFuncArgs) => boolean;
type RejectPromiseFunc = (reason?: any) => void;
type ActiveCallbackPromise = {
	promise: Promise<void>;
	onRouteContinueCallback?: OnRouteContinueCallback;
	onPerformanceMetricStart?: OnPerformanceMetricStartCallback;
	reject: RejectPromiseFunc;
	isExternallyRejected: boolean;
};

export class ExternalRejectionError extends Error {
	constructor(message?: string) {
		super(message);
		this.name = 'ExternalRejectionError';
	}
}

// TODO MODES-6531 Implement timeouts into preloader callback promises. If promise takes too long it should be rejected so it doesn't indefinitely block route changes.
/**
 * GlobalTransitionPreloader is responsible for managing transition callbacks during route changes.
 * These callbacks can be useful if you need to execute some preloading logic before certain route transitions finish, e.g. live doc -> live doc transitions.
 * It ensures that callbacks are executed only when necessary and prevents outdated route changes
 * from being triggered due to quick successive navigations.
 *
 * This class maintains a list of registered callbacks, their unique identifiers, and conditions
 * under which they should be executed. It also handles the lifecycle of promises associated with
 * these callbacks to ensure proper synchronization and error handling.
 */
export class GlobalTransitionPreloader {
	private logger = getLogger('GlobalTransitionPreloader');
	private callbacks: Map<string, CallbackConfig> = new Map();
	private activelyRunningCallbackPromises: Record<string, ActiveCallbackPromise> = {};
	private latestPathname: string = '';
	private currentRouteDetails: { transitionId: number; callbackIds: string[] } = {
		transitionId: -1,
		callbackIds: [],
	};
	private isTransitionCallbackRunning = false;

	/**
	 * Registers a transition callback that will be awaited on a route change.
	 * The callback will only be invoked if the `onlyCalledIfFunc` returns true or if `onlyCalledIfFunc` is not provided. e.g. To check that a contentId is what you expect it should be. `onlyCalledIfFunc` should be very performant as it will run every route change.
	 *
	 * PERFORMANCE WARNING: `onlyCalledIfFunc` is executed every route change, and `callback` promise is awaited every time `onlyCalledIfFunc` returns `true` (or if it doesn't exist).
	 *                      If either of these functions are not very performant, it could cause undesirable performance degradation on every transition.
	 *                      Try to avoid any time-intensive invocations, especially in `onlyCalledIfFunc`. `onlyCalledIfFunc` ideally only runs a condition the `match` that it's passed, e.g. to check `match.params.contentId` or `match.pathname`.
	 *                      BEFORE REGISTERING A NEW TRANSITION CALLBACK, you must be confident that the negative performance impact is acceptable.
	 *
	 * @example registerTransitionCallback({
	 *     id: 'unique-id-related-to-my-code',
	 *     callback: ({match}) => {
	 *         // Do preloading logic, must return a promise
	 *         return new Promise((resolve) => {
	 *             const nextContentId = match.params?.contentId;
	 *             // do logic that is a hard pre-requisite for next route and cannot be lazy/concurrently loaded, ideally shouldn't be slow
	 *             resolve(); // route change gets unblocked after these callback promises are resolved
	 *         }
	 *     },
	 *     onlyCalledIfFunc: ({match}) => {
	 *         // Optional but recommended
	 *         const nextContentId = match.params?.contentId;
	 *         // Do very quick logic, ideally no logic that we need to wait for, this function is run every route change
	 *         if (isLiveDoc(contentId)) {
	 *             return true;
	 *         } else {
	 *             return false;
	 *         }
	 *     },
	 *     onPerformanceMetricStart: ({match}) => {
	 *         // Optional but recommended
	 *         const nextContentId = match.params?.contentId;
	 *         // Should be used to quickly start any page load performance metrics that you want to track for this route change
	 *         // If you don't use this, your existing page load metric will likely start after the preloading and after the route change.
	 *         // We want page load metrics to start before preloading, not after.
	 *     },
	 *     onRouteContinueCallback: async ({match}) => {
	 *		   // Optional, must return a promise (i.e. or be async)
	 *         const nextContentId = match.params?.contentId;
	 *         // Do very quick logic that will run only if we're about to transition routes, runs after callback promise.
	 *         // Can be used to do logic that you want to ensure doesn't occur on outdated route changes, e.g. connecting to nextContentId's collab provider
	 *     },
	 * });
	 *
	 * @param {Object} params - The parameters for the transition callback.
	 * @param {string} params.id - A unique identifier to prevent duplicate callback registrations.
	 * @param {TransitionCallback} params.callback - The callback function to be executed during a route transition. Must return a promise.
	 * @param {OnlyCalledIfFunc} params.onlyCalledIfFunc - A function that determines whether the callback should be called.
	 * @param {OnRouteContinueCallback} params.onRouteContinueCallback - Callback function that must return a promise. This is called right after the transition callback promises finish but before we continue the route transition, the returned promise blocks route transition. This function won't be called if route has changed before callback promise resolves.
	 * @param {OnPerformanceMetricStartCallback} params.onPerformanceMetricStart - Callback function to start performance metrics. Called as we start running the transition callback promises.
	 * @returns {boolean} - Returns `true` if the transition callback was registered, `false` if it was not registered (due to already being registered or being mid-transition).
	 */
	registerTransitionCallback({
		id,
		callback,
		onlyCalledIfFunc,
		onRouteContinueCallback,
		onPerformanceMetricStart,
	}: {
		id: string;
		callback: TransitionCallback;
		onlyCalledIfFunc?: OnlyCalledIfFunc;
		onRouteContinueCallback?: OnRouteContinueCallback;
		onPerformanceMetricStart?: OnPerformanceMetricStartCallback;
	}): boolean {
		// We want to keep transition callbacks unique to guarantee we aren't duplicating work, so we tie callbacks to an ID. If there are multiple registrations of the same callback ID, we ignore registration attempts if we're already registered.
		// We don't allow registering transition callbacks while transitioning, to avoid race conditions leading to infinite route change loops.
		if (this.isTransitionCallbackRunning || this.callbacks.has(id)) {
			return false;
		}

		this.callbacks.set(id, {
			callback,
			onlyCalledIfFunc,
			onRouteContinueCallback,
			onPerformanceMetricStart,
		});
		return true;
	}

	/**
	 * Processes transition callbacks during a route change, checking if we need to create promises from callbacks, and waiting for them to resolve/reject before continuing the route change.
	 * `processTransitionCallback` will set `this.isTransitionCallbackRunning` to true, preventing callback registration. RouteManager is responsible for clearing it via `onTransitionComplete`.
	 *
	 * @param {{ match: Match; onRouteContinue: () => void; transitionId: number; interactionContext?: UFOInteractionContextType | null }} args - The arguments containing the route `match` object, RouteManager `transitionId`, the `onRouteContinue` continuation function, and optional UFO interaction context.
	 *                                                                                     `onRouteContinue` is called once promises resolve and should finish executing the route change.
	 * @returns {boolean} - Returns `true` if processing transition callback and transition should not continue (until promise finishes and calls onRouteContinue).
	 *                      Returns `false` if not processing transition callback and routeChangeHandler should continue running.
	 */
	processTransitionCallback({
		match,
		transitionId,
		onRouteContinue,
		interactionContext,
		startPageLoadMetrics,
	}: {
		match: Match;
		transitionId: number;
		onRouteContinue: () => void;
		interactionContext?: UFOInteractionContextType | null;
		startPageLoadMetrics?: (
			name: string,
			ufoName: string | undefined,
			transitionId: number,
			contentId?: string,
		) => void;
	}): { isProcessingCallback: boolean; ufoStopHold?: () => void } {
		try {
			if (!this.callbacks.size) {
				// Update latest route details to keep this.getCurrentRouteCallbackIds() is accurate. If transitionId has changed, accurately reflect that there are no callbackIds for that route change.
				if (transitionId !== this.currentRouteDetails.transitionId) {
					this.currentRouteDetails = {
						transitionId,
						callbackIds: [],
					};
				}
				return { isProcessingCallback: false };
			}

			this.isTransitionCallbackRunning = true;

			const previousPathname = this.latestPathname;
			this.latestPathname = match?.pathname;
			if (this.latestPathname !== previousPathname) {
				// Cancel all currently running promises because we're going to a different route. Helps prevent multiple quick route changes from causing race condition that could send user to outdated route change.
				// If needed, `getCallbackPromises` below will create new promises for the latest route.
				this.cancelActivePromises();
			}

			const { callbackPromises, callbackPromiseIds, callbackPromiseObjects } =
				this.getCallbackPromises({ match });
			if (!callbackPromises.length) {
				// If there are no callback promises, route change will immediately continue. Reset our callbacks state as we don't want registered callbacks to linger once the user leaves the route. Callbacks should be registered on page/etc load if needed.
				// EDGE CASE: If the user is on a live doc and quickly clicks a non-live-doc like a view page, then quickly clicks back into a live doc before the view page has loaded, the preload-live-doc callback may have been removed, slowing down load time into that live doc.
				//            We'll solve for this in the future by always checking if we should preload a live doc on any route change, by never unregistering that callback
				this.reset();

				return { isProcessingCallback: false };
			}

			// Start browser metrics page load
			startPageLoadMetrics?.(
				match?.name ?? '',
				match?.ufoName,
				transitionId,
				match?.params?.contentId,
			);

			// Ensure UFO page load metric continues tracking the page load from start of preloading. If we don't hold here, UFO metric finishes prematurely on route change.
			// We should stop holding right after the route change is completed.
			const ufoStopHold = interactionContext?.hold?.('GlobalTransitionPreloader');

			callbackPromiseObjects.forEach(({ onPerformanceMetricStart }) => {
				onPerformanceMetricStart?.({ match });
			});

			// Timeout for all promises to prevent indefinite waiting
			const TIMEOUT_MS = 10000; // 10 seconds
			let timeoutId: ReturnType<typeof setTimeout> | null = null;

			// Flag to prevent race conditions between timeout and promise completion
			// Only one of them should trigger the route transition
			let hasRouteTransitionCompleted = false;

			// Set up timeout if feature gate is enabled
			if (fg('confluence_live_doc_smooth_transition_timeout')) {
				timeoutId = setTimeout(() => {
					// Only execute if route transition hasn't been completed by promise resolution
					if (!hasRouteTransitionCompleted) {
						hasRouteTransitionCompleted = true;
						this.logger
							.error`Callback promises timed out after ${TIMEOUT_MS}ms, forcing route transition`;
						this.reset();
						onRouteContinue();
						void getAnalyticsWebClient().then((analyticsWebClient) => {
							analyticsWebClient.sendOperationalEvent({
								action: 'confluence_live_doc_smooth_transition_timeout',
								actionSubject: 'callback_promises_timed_out',
								attributes: {
									callbackPromiseIds: callbackPromiseIds.join(','),
								},
								source: 'GlobalTransitionPreloader',
							});
						});
					}
				}, TIMEOUT_MS);
			}

			// Handle all the promises and then send the user to the next route if appropriate.
			void Promise.allSettled(callbackPromises).then((promiseResults) => {
				// Only proceed if timeout hasn't already triggered the route transition
				if (hasRouteTransitionCompleted) {
					return;
				}

				// Mark that we're handling the route transition to prevent timeout from also executing
				hasRouteTransitionCompleted = true;

				// Clear timeout if callbacks completed first
				if (timeoutId) {
					clearTimeout(timeoutId);
				}

				const onRouteContinueCallbacks: Array<OnRouteContinueCallback> = [];
				let externallyRejectedCount = 0;
				promiseResults.forEach((result, index) => {
					if (result.status === 'rejected') {
						if (callbackPromiseObjects[index].isExternallyRejected) {
							externallyRejectedCount++;
						} else {
							this.logger
								.error`There was an error with a transition callback promise: ${result.reason}, active callbackPromiseIds: ${callbackPromiseIds.join(',')}`;
						}
					} else {
						const { onRouteContinueCallback } = callbackPromiseObjects[index];
						if (onRouteContinueCallback) {
							onRouteContinueCallbacks.push(onRouteContinueCallback);
						}
					}
				});

				if (externallyRejectedCount < callbackPromises.length) {
					const onRouteContinueCallbackPromises = onRouteContinueCallbacks.map((callback) =>
						callback({ match }),
					);
					this.currentRouteDetails.callbackIds = callbackPromiseIds;
					this.currentRouteDetails.transitionId = transitionId;

					// On successful next route, we clear the currently registered callbacks. Callbacks should be registered on page/etc load if needed.
					this.callbacks = new Map();
					this.activelyRunningCallbackPromises = {};

					void Promise.allSettled(onRouteContinueCallbackPromises).then(() => {
						// If all promises have been externally rejected, that means these promises are for an outdated route change, the user has since changed routes again, so we don't want to redirect the user to the route.
						// This avoids race condition scenarios where user goes from Doc A -> Doc B -> Doc C, but ends up on Doc B because its promises finished after Doc C's promises.
						// On successful next route, we clear the currently registered callbacks. Callbacks should be registered on page/etc load if needed.
						this.callbacks = new Map();
						this.activelyRunningCallbackPromises = {};

						onRouteContinue();
					});
				}
			});

			return {
				isProcessingCallback: true,
				ufoStopHold: ufoStopHold ? () => ufoStopHold?.() : undefined,
			};
		} catch (error) {
			this.logger
				.error`There was an error running processTransitionCallback: ${error}, registered callbackIds: ${Array.from(this.callbacks.keys()).join(',')}`;
		}

		return { isProcessingCallback: false };
	}

	/**
	 * Use these current route callback IDs to determine whether they were ran on your current route or not. Can be useful to determine if your page was already preloaded or not.
	 *
	 * @returns {string[]} - List of registered transition callback IDs that ran on current route when transitioning into it
	 */
	getCurrentRouteCallbackIds(): string[] {
		return [...this.currentRouteDetails.callbackIds];
	}

	/**
	 * Resets the preloader to its initial state by clearing all registered callbacks,
	 * canceling any actively running promises, and resetting internal state.
	 * This can be useful for cleanup or testing scenarios.
	 */
	reset(): void {
		this.cancelActivePromises();

		this.callbacks = new Map();
		this.activelyRunningCallbackPromises = {};
		this.latestPathname = '';
		this.currentRouteDetails.callbackIds = [];
	}

	/**
	 * Creates an array of promises from registered callbacks that should be executed
	 * during a route transition. This method checks conditions under which each callback
	 * should be called and manages the lifecycle of the promises.
	 *
	 * @private
	 * @param { match: Match } params - The parameters for the method, containing the match object.
	 * @returns { callbackPromises: Array<Promise<void>>, callbackPromiseIds: Array<string> } An object containing an array of promises and their corresponding IDs.
	 */
	private getCallbackPromises({ match }: { match: Match }): {
		callbackPromises: Array<Promise<void>>;
		callbackPromiseIds: Array<string>;
		callbackPromiseObjects: Array<ActiveCallbackPromise>;
	} {
		if (!this.callbacks) {
			return { callbackPromises: [], callbackPromiseIds: [], callbackPromiseObjects: [] };
		}

		const callbackPromises: Array<Promise<void>> = [];
		const callbackPromiseIds: Array<string> = []; // Recommendation is to use this to help provide context to promise errors
		const callbackPromiseObjects: Array<ActiveCallbackPromise> = [];

		this.callbacks.forEach(
			(
				{ callback, onlyCalledIfFunc, onRouteContinueCallback, onPerformanceMetricStart },
				callbackId,
			) => {
				// Call our `onlyCalledIfFunc` if it's set, we're only waiting for this callback promise if it's `true` or if it's not set.
				const isCallAllowed = onlyCalledIfFunc ? onlyCalledIfFunc({ match }) : true;

				// If we want to trigger a callback promise, return the already running one if we have it, otherwise create a new one.
				if (isCallAllowed) {
					const activeRunningPromise = this.activelyRunningCallbackPromises[callbackId];
					if (activeRunningPromise?.promise) {
						callbackPromises.push(activeRunningPromise.promise);
						callbackPromiseIds.push(callbackId);
						callbackPromiseObjects.push(activeRunningPromise);
					} else {
						// Start the promise and store the promise and its reject function in state (this.processTransitionCallback may call `reject` if the user changes route)
						let callbackPromiseReject: RejectPromiseFunc;
						const callbackPromise = new Promise<void>((resolve, reject) => {
							callbackPromiseReject = reject;
							callback({ match }).then(resolve).catch(reject);
						});
						callbackPromises.push(callbackPromise);
						callbackPromiseIds.push(callbackId);
						const callbackPromiseObject = {
							promise: callbackPromise,
							onRouteContinueCallback,
							onPerformanceMetricStart,
							reject: (reason: string) => callbackPromiseReject(new ExternalRejectionError(reason)),
							isExternallyRejected: false,
						};
						callbackPromiseObjects.push(callbackPromiseObject);
						this.activelyRunningCallbackPromises[callbackId] = callbackPromiseObject;
					}
				} else if (this.activelyRunningCallbackPromises[callbackId]) {
					// If the promise no longer needs to run then stop tracking it if it's already running.
					this.activelyRunningCallbackPromises[callbackId]?.reject?.();
					delete this.activelyRunningCallbackPromises[callbackId];
				}
			},
		);
		return { callbackPromises, callbackPromiseIds, callbackPromiseObjects };
	}

	/**
	 * Router is responsible for running this function when the route has technically changed (routeChangeHandler).
	 *
	 * When GlobalTransitionPreloader processes transition callbacks, it sets its transition running flag to true.
	 * When it's done processing transition callbacks and the route has changed, we should clear the transition running flag.
	 * This is used to prevent race conditions caused by problematic globalTransitionPreloader callback registrations during route changes.
	 * For instance: If a callback is registered mid-transition, then when `routeBlockHandler` is called a second time by `onRouteContinue`,
	 *               processTransitionCallback may try to process it after it has already processed it, leading to an infinite loop.
	 *
	 * @param {number} transitionId - The transition ID of the route that has completed, coming from RouteManager
	 */
	onTransitionComplete(transitionId: number): void {
		this.isTransitionCallbackRunning = false;

		// Transition ID from RouteManager is stored in this.currentRouteDetails before we call `routeChangeHandler`.
		// If we processed a transition callback, it'll always be one behind RouteManager
		// If transition IDs are misaligned and currentRouteDetails.callbackIds is non-empty, we're in an invalid state likely due to routeBlockHandler/processTransitionCallback unexpectedly not being triggered
		// If we're in this invalid state, reset the current route details callback IDs to prevent components from thinking we're in a preloaded state when we're not.
		if (
			this.currentRouteDetails.transitionId + 1 !== transitionId &&
			this.currentRouteDetails.callbackIds.length > 0
		) {
			this.logger
				.error`GlobalTransitionPreloader is in an invalid state, transitionId mismatch, callbackIds are non-empty. Resetting callbackIds. Invalid callbackIds are: ${this.currentRouteDetails.callbackIds.join(',')}`;
			this.currentRouteDetails.callbackIds = [];
		}
	}

	/**
	 * Cancels all actively running callback promises by rejecting them and marking as externally rejected
	 */
	private cancelActivePromises(): void {
		Object.entries(this.activelyRunningCallbackPromises).forEach(([callbackId, { reject }]) => {
			reject();
			this.activelyRunningCallbackPromises[callbackId].isExternallyRejected = true;
			delete this.activelyRunningCallbackPromises[callbackId];
		});
	}
}
